import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Modal, Form, Select, Input, Switch, Popconfirm, Space, Typography, Image, message, DatePicker, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const linkTypeOptions = [
  { value: 'url', label: '外部链接' },
  { value: 'mini_program', label: '小程序' },
  { value: 'page', label: '内部页面' },
];

const linkTypeLabels = {
  url: { label: '外部链接', color: 'blue' },
  mini_program: { label: '小程序', color: 'green' },
  page: { label: '内部页面', color: 'purple' },
};

const positionOptions = [
  { value: 'home', label: '首页' },
  { value: 'other', label: '其他' },
];

function BannerPage() {
  const [banners, setBanners] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadBanners = async (p = 1) => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/banners', { params: { page: p, limit: 20 } });
      setBanners(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      message.error('加载Banner列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBanners(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        start_time: values.time_range?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.time_range?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        time_range: undefined,
      };
      await api.post('/admin/banners', payload);
      message.success('Banner创建成功');
      setCreateModal(false);
      form.resetFields();
      loadBanners(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('创建失败');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      ...record,
      time_range: record.start_time && record.end_time
        ? [dayjs(record.start_time), dayjs(record.end_time)]
        : null,
    });
    setEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        ...values,
        start_time: values.time_range?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.time_range?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        time_range: undefined,
      };
      await api.put(`/admin/banners/${editingRecord.id}`, payload);
      message.success('更新成功');
      setEditModal(false);
      loadBanners(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/banners/${id}`);
      message.success('Banner已删除');
      loadBanners(page);
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (id, status) => {
    try {
      await api.put(`/admin/banners/${id}`, { status: status ? 1 : 0 });
      message.success(status ? '已启用' : '已禁用');
      loadBanners(page);
    } catch (e) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '标题', dataIndex: 'title', width: 200 },
    {
      title: '图片预览', width: 160,
      render: (_, r) => (
        <Image
          src={r.image_url}
          width={120}
          height={50}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
        />
      ),
    },
    {
      title: '链接类型', dataIndex: 'link_type', width: 120,
      render: (v) => <Tag color={linkTypeLabels[v]?.color}>{linkTypeLabels[v]?.label || v}</Tag>,
    },
    {
      title: '位置', dataIndex: 'position', width: 100,
      render: (v) => <Tag color={v === 'home' ? 'blue' : 'default'}>{v === 'home' ? '首页' : v || '其他'}</Tag>,
    },
    { title: '排序', dataIndex: 'sort_order', width: 80, render: (v) => v ?? 0 },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (v, r) => (
        <Switch
          checked={v === 1}
          onChange={(checked) => handleToggleStatus(r.id, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: '展示时间', width: 280,
      render: (_, r) => (
        <span>
          {r.start_time ? new Date(r.start_time).toLocaleString() : '-'} ~<br />
          {r.end_time ? new Date(r.end_time).toLocaleString() : '-'}
        </span>
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 180,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该Banner?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>Banner管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ position: 'home', sort_order: 0, link_type: 'url' }); setCreateModal(true); }} style={{ background: '#07C160' }}>
          添加Banner
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={banners}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: loadBanners,
            showSizeChanger: false,
          }}
          size="small"
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* 创建 Banner Modal */}
      <Modal title="添加Banner" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="Banner标题" />
          </Form.Item>
          <Form.Item name="image_url" label="图片地址" rules={[{ required: true, message: '请输入图片URL' }]}>
            <Input placeholder="https://example.com/banner.png" />
          </Form.Item>
          {form.getFieldValue('image_url') && (
            <Form.Item label="预览">
              <Image
                src={form.getFieldValue('image_url')}
                width={300}
                height={120}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                fallback=""
              />
            </Form.Item>
          )}
          <Form.Item name="link_type" label="链接类型" rules={[{ required: true }]} initialValue="url">
            <Select placeholder="选择链接类型">
              {linkTypeOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="link_url" label="跳转地址" rules={[{ required: true, message: '请输入跳转地址' }]}>
            <Input placeholder="跳转URL或路径" />
          </Form.Item>
          <Form.Item name="link_target" label="链接目标 (仅小程序)">
            <Input placeholder="小程序页面路径，如: /pages/index/index" />
          </Form.Item>
          <Form.Item name="position" label="展示位置" rules={[{ required: true }]} initialValue="home">
            <Select placeholder="选择展示位置">
              {positionOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="time_range" label="展示时间">
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="越小越靠前" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑 Banner Modal */}
      <Modal title="编辑Banner" open={editModal} onOk={handleUpdate} onCancel={() => setEditModal(false)} width={600}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="Banner标题" />
          </Form.Item>
          <Form.Item name="image_url" label="图片地址" rules={[{ required: true, message: '请输入图片URL' }]}>
            <Input placeholder="https://example.com/banner.png" />
          </Form.Item>
          {editForm.getFieldValue('image_url') && (
            <Form.Item label="预览">
              <Image
                src={editForm.getFieldValue('image_url')}
                width={300}
                height={120}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                fallback=""
              />
            </Form.Item>
          )}
          <Form.Item name="link_type" label="链接类型" rules={[{ required: true }]}>
            <Select placeholder="选择链接类型">
              {linkTypeOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="link_url" label="跳转地址" rules={[{ required: true, message: '请输入跳转地址' }]}>
            <Input placeholder="跳转URL或路径" />
          </Form.Item>
          <Form.Item name="link_target" label="链接目标 (仅小程序)">
            <Input placeholder="小程序页面路径，如: /pages/index/index" />
          </Form.Item>
          <Form.Item name="position" label="展示位置" rules={[{ required: true }]}>
            <Select placeholder="选择展示位置">
              {positionOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="time_range" label="展示时间">
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="越小越靠前" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default BannerPage;
